// ============================================================
// Leave module manifest — R3 retrofit (2026-04-24).
//
// Owns leave types catalog + leave entitlements + leave requests,
// plus the Malaysian public-holiday integration that drives leave-
// day computation. Depends on hr (employee resolution).
// ============================================================

export default {
  id:          'leave',
  name:        'Leave',
  version:     '1.0.0',
  description: 'Leave types, entitlements, requests, approvals; Malaysian public holidays integration.',

  load: () => import('./index.js'),

  routes: [
    {
      id:       'leave-management',
      // 2026-04-28 — DISCOVERABILITY FIX (DESIGN_LOG Decision 1A).
      // Per-role label resolved at sidebar-render time. Staff see the verb
      // ("Apply Leave") which matches what the page IS for them; HR/director/
      // admin see "Leave Management" which matches what the page IS for them
      // (an approval queue). main.js sidebar render accepts either string or
      // (role) => string for `label` — backward compat preserved.
      label:    (role) => (role === 'staff' ? 'Apply Leave' : 'Leave Management'),
      icon:     '📅',
      section:  'people',
      sidebarPosition: 20,
      // 2026-05-04 sidebar-leak fix — gate on `leave.read.self` (the
      // minimum perm to land on this page; staff get it, HR + director
      // get the broader leave.* wildcard). Page-level branching shows
      // staff their own balance + apply form, and HR/director the
      // approval queue (see leave-management.js).
      visible:  (role, hasPerm) => hasPerm('leave.read.self'),
      mount:    'mountLeaveManagement',
      unmount:  'unmountLeaveManagement'
    }
  ],

  schemas: [
    'leavetypes',          // catalog (optional override of LEAVE_TYPES code constant)
    'leaveentitlements',   // per (employee, year, type)
    'leaverequests',       // application records
    'publicholidays'       // Malaysian gazetted dates (also used by payroll for unpaid-leave deductions later)
  ],

  permissions: [
    'leave.read.self',     // see own balance + own requests
    'leave.read.all',      // see everyone's queue (HR + director)
    'leave.apply',         // submit a request
    'leave.approve',       // approve / reject (HR + director)
    'leave.cancel.self',   // self-cancel a Pending request
    'leave.cancel.any',    // HR cancels post-approval
    'leave.holidays.write' // edit the public-holidays catalog (admin)
  ],

  // Leave depends on hr — employee resolution (fetchMyEmployee, which
  // tries AuthEmail then LoginUid) is hr's surface; entitlement seeding
  // fires on employee create.
  dependencies: ['hr'],

  fitsIndustries: ['manufacturing', 'trading', 'services', 'retail'],

  functions: [],

  // Dashboard widgets — pending-leave queue + on-leave-today (HR + director).
  // 2026-04-28 — added staff-only "Apply Leave" CTA tile per DESIGN_LOG
  // Decision 1B (discoverability — extra entry-point so staff see it on first
  // landing without needing the sidebar). Lives in the KPI strip with order:5
  // so it's the leftmost/first thing a staff user sees on the dashboard.
  dashboardWidgets: [
    {
      id:           'leave-apply-cta',
      label:        'Apply Leave',
      section:      'kpi',
      order:        5,
      defaultRoles: ['staff']
    },
    {
      id:           'leave-pending-kpi',
      label:        'Pending leave',
      section:      'hr-block',
      order:        20,
      defaultRoles: ['director', 'hr']
    },
    {
      id:           'leave-on-leave-today-kpi',
      label:        'On leave today',
      section:      'hr-block',
      order:        30,
      defaultRoles: ['director', 'hr']
    }
  ]
};
